import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { AppDispatch } from '../../app/store'
import {
  removeCartItemFromApi,
  updateCartItemQuantity,
  selectIsLoading,
  selectIsUpdatingQuantity,
  selectUpdatingItemId,
  selectIsDeletingItem,
  selectDeletingItemId
} from '../../features/cart/cartItemsSlice'
import { selectIsCsCartDeletingItem, selectCsCartDeletingItemId } from '../../features/csFlow/csCartSlice'
import { Plus, Minus, Loader2 } from 'lucide-react'
import { CartItem } from '../../types'
import dalaSvg from '../../assets/images/salle.svg'
import deletes from '../../assets/images/delete.svg'
import CurrencyDisplay from '../shared/CurrencyDisplay'

interface CartItemsSectionProps {
  items: CartItem[]
  currency: string
}

const CartItemsSection: React.FC<CartItemsSectionProps> = ({
  items,
  currency
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch<AppDispatch>()

  const isLoading = useSelector(selectIsLoading)
  const isUpdatingQuantity = useSelector(selectIsUpdatingQuantity)
  const updatingItemId = useSelector(selectUpdatingItemId)
  const isDeletingItem = useSelector(selectIsDeletingItem)
  const deletingItemId = useSelector(selectDeletingItemId)
  
  // Check if user is CS
  const isCustomerService = React.useMemo(() => {
    try {
      const employeeDataStr = localStorage.getItem('employee_data')
      if (employeeDataStr) {
        const employeeData = JSON.parse(employeeDataStr)
        const userType = employeeData.user_type || employeeData.type || ''
        return userType.toString().toLowerCase().includes('cs') ||
          userType.toString().toLowerCase().includes('customer_service') ||
          userType.toString().toLowerCase() === 'customer_service'
      }
      return false
    } catch (e) {
      return false
    }
  }, [])
  
  // Get deleting state based on user type
  const isCsCartDeletingItem = useSelector(selectIsCsCartDeletingItem)
  const csCartDeletingItemId = useSelector(selectCsCartDeletingItemId)
  const isAnyDeleting = isCustomerService ? isCsCartDeletingItem : isDeletingItem

  const handleQuantityChange = (itemId: string, newQuantity: number) => {
    if (!itemId.trim()) return
    if (newQuantity < 1) return
    dispatch(updateCartItemQuantity({ itemId, quantity: newQuantity }) as any)
  }

  const handleRemove = (itemId: string) => {
    if (!itemId.trim()) return
    dispatch(removeCartItemFromApi(itemId) as any)
  }

  // Extract Size
  const getSize = (item: CartItem) => {
    try {
      if (item.meals?.length) {
        return item.meals[0]?.size?.name || item.meals[0]?.size || ''
      }
      return item.size?.name || item.size || ''
    } catch {
      return ''
    }
  }

  // Extract extras
  const getAdditionals = (item: CartItem) => {
    try {
      if (Array.isArray(item.additionals)) return item.additionals

      if (item.meals?.length) {
        const meal = item.meals[0]
        if (meal.additionalsGroups?.length) {
          return meal.additionalsGroups.flatMap((g) => g.additionals || [])
        }
      }

      return item.modifiers || []
    } catch {
      return []
    }
  }

  return (
    <div className="h-full bg-white rounded-xl p-4 relative overflow-y-auto">

      {(isLoading || isUpdatingQuantity || isAnyDeleting) && (
        <div className="absolute inset-0 bg-white backdrop-blur-sm z-10 flex items-center justify-center rounded-xl">
          <Loader2 className="w-6 h-6 animate-spin text-flutter-button" />
        </div>
      )}

      {items.length > 0 ? (
        items
          .filter((item) => (item.id || item.product_id)?.toString().trim())
          .map((item, index) => {
            const itemId =
              item.id?.toString() || item.product_id?.toString() || ''

            const isItemUpdating =
              updatingItemId === itemId || updatingItemId === item.id
            const isItemDeleting =
              deletingItemId === itemId || deletingItemId === item.id || 
              (isCustomerService && (csCartDeletingItemId === itemId || csCartDeletingItemId === item.id))

            const size = getSize(item)
            const additionals = getAdditionals(item)
            const sizeLabel = size?.toString().trim()
            const pillList: { name: string; isSize?: boolean }[] = [
              ...(sizeLabel ? [{ name: sizeLabel, isSize: true }] : []),
              ...(Array.isArray(additionals)
                ? additionals.map((a) => {
                    if (typeof a === 'string') return { name: a }
                    const name =
                      a.name ||
                      a.title ||
                      a.addon_name ||
                      a.additional_name ||
                      String(a)
                    return { name }
                  })
                : [])
            ]

            const itemName = item.product?.name || t('home.beef_burger')
            const itemImage =
              item.product?.image || 'https://placehold.co/56x56'
            const itemPrice =
              item.total_price || item.price * item.quantity || 0

            // Create a stable key that combines item ID with product ID to ensure uniqueness
            // This prevents React from reusing components incorrectly when IDs change format
            const stableKey = itemId 
              ? `${itemId}_${item.product_id || item.product?.id || index}`
              : `item_${index}_${item.product_id || item.product?.id || Date.now()}`
            
            return (
              <div
                key={stableKey}
                className={`w-full mb-4 ${isItemUpdating || isItemDeleting ? 'opacity-50' : ''}`}
                dir="rtl"
              >
                <div className="w-full bg-white p-0 rounded-xl  flex flex-col gap-3">

                  {/* === TOP ROW: DELETE + QUANTITY === */}
                  <div className="flex items-center justify-between w-full">

                  


                    <div className="flex items-center gap-2">
  {/* Image */}
                    <img
                      src={itemImage}
                      className="w-14 h-14 rounded-full object-cover"
                      loading="lazy"
                      onError={(e) =>
                        (e.currentTarget.src = 'https://placehold.co/56x56')
                      }
                    />
                        {/* Name + Price */}
                    <div className="flex flex-col items-start gap-1">
                      <div className="text-base font-medium text-[#525866]">
                        {itemName}
                      </div>

                      <div className="flex items-center gap-1">
                        <span className="text-base font-semibold text-[#525866]">
                          {itemPrice.toFixed(2)}
                        </span>
                        <CurrencyDisplay className="w-4 h-4" />
                      </div>
                    </div>
                    </div>
             <div className="flex items-center gap-2">
                    {/* Quantity Controls */}
                    <div className="h-10 rounded-xl border border-[#E1E4EA] flex items-center bg-white overflow-hidden relative">
                      {isItemUpdating && (
                        <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
                          <Loader2 className="w-4 h-4 animate-spin text-flutter-button" />
                        </div>
                      )}

                      <button
                        onClick={() =>
                          handleQuantityChange(itemId, item.quantity - 1)
                        }
                        disabled={item.quantity <= 1 || isUpdatingQuantity}
                        className="w-10 h-full flex items-center justify-center border-l border-[#E1E4EA] hover:bg-gray-50 disabled:opacity-50"
                      >
                        <Minus className="w-5 h-5 text-[#525866]" />
                      </button>

                      <div className="w-10 h-full flex items-center justify-center">
                        <span className="text-base font-medium text-[#525866]">
                          {item.quantity}
                        </span>
                      </div>

                      <button
                        onClick={() =>
                          handleQuantityChange(itemId, item.quantity + 1)
                        }
                        className="w-10 h-full flex items-center justify-center border-r border-[#E1E4EA] hover:bg-gray-50 disabled:opacity-50"
                      >
                        <Plus className="w-5 h-5 text-[#525866]" />
                      </button>
                    </div>
 {/* Delete Button */}
 <button
                      onClick={() => handleRemove(itemId)}
                      disabled={isLoading || isAnyDeleting}
                      className="h-[40px] w-[40px] border border-gray-300 rounded-xl flex items-center justify-center hover:bg-gray-50 disabled:opacity-50"
                    >
                      {isItemDeleting ? (
                        <Loader2 className="w-5 h-5 animate-spin text-flutter-button" />
                      ) : (
                        <img src={deletes} className="w-5 h-5" />
                      )}
                    </button>
             </div>
                  </div>

                  {/* === ADDITIONALS PILLS === */}
                  {!!pillList.length && (
                    <div className="flex flex-wrap gap-2">
                      {pillList.map((pill, i) => {
                        const isSizePill = Boolean(pill.isSize)
                        const name = pill.name || ''
                        const pillClasses = [
                          'px-2.5 py-0.5 rounded-full border flex items-center justify-center',
                          isSizePill
                            ? 'border-amber-200 bg-amber-50 text-amber-700 font-semibold'
                            : 'border-gray-200 bg-white text-[#525866]'
                        ].join(' ')

                        return (
                          <div key={i} className={pillClasses}>
                            <span className="text-xs leading-4">{name}</span>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>
            )
          })
      ) : (
        <div className="flex flex-col items-center justify-center h-full">
          <img src={dalaSvg} className="w-14 h-14 mb-2" />
          <p className="text-base text-gray-700">{t('cart.cart_empty')}</p>
          <p className="text-xs text-gray-500">{t('cart.add_items_to_cart')}</p>
        </div>
      )}
    </div>
  )
}

export default CartItemsSection
